import { TELEFON } from './mobil';

/**
 * TABLO → KART (telefon, ≤640 px)
 *
 * Paneldeki bütün `table.ccd-table`'lar telefonda satır yerine KART olarak çizilir:
 * her hücre "etiket: değer", ilk sütun kart başlığı, eylem düğmeleri kartın altında.
 * Görünüm tamamen CSS'tedir (app.css › "Tablo → kart"); CSS'in ihtiyaç duyduğu etiketler
 * hücrelere `data-etiket` olarak yazılır.
 *
 *  · `ui/Rapor.tsx › Tablo` etiketleri kendi çizer (`data-kart-hazir`); burada dokunulmaz.
 *  · Elle yazılmış tablolar için bu dosya başlık satırını okuyup hücreleri etiketler.
 *    React'in yönettiği düğümlere yalnız `data-*` özniteliği eklenir; düğüm eklenmez/silinmez.
 *  · Kart olması anlamsız tablolar (ay × kalem matrisi gibi) `data-mobil="tablo"` ile
 *    dışarıda tutulur: kendi kabında yatay kayar.
 *  · Çok sütunlu tabloda ilk `data-mobil-ana` (varsayılan 4) sütun görünür; kalanı karta
 *    dokununca açılır (satır zaten tıklanabilirse — ayrıntı çekmecesi açıyorsa — gizli kalır,
 *    çünkü ayrıntı orada).
 */
const VARSAYILAN_ANA = 5;
const COK_SUTUN = 7;

const baslikMetni = (th: Element): string => {
  // «?» ipucu düğmesi, sıralama oku gibi yardımcılar etikete girmez.
  let m = '';
  for (const n of th.childNodes) {
    if (n.nodeType === 3) m += n.textContent ?? '';
    else if (n.nodeType === 1 && !(n as Element).matches('.ccd-fipucu, svg, [aria-hidden="true"]')) m += baslikMetni(n as Element) || '';
  }
  m = m.replace(/[↑↓↕▲▼]/g, '').replace(/\s+/g, ' ').trim();
  return m || (th.getAttribute('aria-label') ?? '').trim();
};

/** Hücre yalnız düğme / eylem bağlantısı taşıyor (başlığı boş ya da "İşlem" gibi bir ad): kartın altına iner. */
function yalnizEylem(td: HTMLTableCellElement, baslik: string): boolean {
  const dugmeler = td.querySelectorAll('button, a.ccd-btn, select');
  if (dugmeler.length === 0) return false;
  if (!baslik) return true;
  let dugmeMetni = 0;
  for (const d of dugmeler) dugmeMetni += (d.textContent ?? '').trim().length;
  return (td.textContent ?? '').trim().length - dugmeMetni <= 1;
}

function tabloyuEtiketle(tablo: HTMLTableElement) {
  if (tablo.dataset.kartHazir !== undefined || tablo.dataset.mobil === 'tablo' || tablo.closest('[data-mobil="tablo"]')) return;
  const basSatir = tablo.tHead?.rows[tablo.tHead.rows.length - 1];
  if (!basSatir) { if (tablo.dataset.kart !== 'yok') tablo.dataset.kart = 'yok'; return; }
  // Sütun sırası → etiket (colspan'lı başlık aynı etiketi birden çok sütuna verir).
  const etiket: string[] = [];
  for (const th of basSatir.cells) { const m = baslikMetni(th); for (let i = 0; i < (th.colSpan || 1); i++) etiket.push(m); }
  const sutun = etiket.length;
  const ana = Number(tablo.dataset.mobilAna) || VARSAYILAN_ANA;
  const cokSutun = sutun >= COK_SUTUN;
  if (tablo.dataset.kart !== 'acik') tablo.dataset.kart = 'acik';
  kutuyuSerbestBirak(tablo.parentElement);

  const yaz = (el: HTMLElement, ad: string, deger: string | undefined) => {
    if (deger === undefined) { if (el.dataset[ad] !== undefined) delete el.dataset[ad]; } else if (el.dataset[ad] !== deger) el.dataset[ad] = deger;
  };

  for (const govde of [...tablo.tBodies, ...(tablo.tFoot ? [tablo.tFoot] : [])]) {
    for (const tr of govde.rows) {
      const hucreler = tr.cells;
      // Tek hücreli tam genişlik satır: grup başlığı, boş durum, açılan ayrıntı.
      if (hucreler.length === 1 && (hucreler[0].colSpan || 1) >= Math.max(2, sutun - 1)) { yaz(tr, 'kartTam', '1'); continue; }
      yaz(tr, 'kartTam', undefined);
      let k = 0; let gizliVar = false; let gorunen = 0;
      let baslikVerildi = [...hucreler].some((td) => td.dataset.kartSabit !== undefined && td.dataset.kart === 'baslik');
      for (const td of hucreler) {
        const ad = etiket[k] ?? '';
        k += td.colSpan || 1;
        // `data-kart-sabit`: türü sayfa (ya da `Tablo` bileşeni) kendisi vermiş; yalnız etiketi yazılır.
        const sabit = td.dataset.kartSabit !== undefined;
        let tur: string | undefined = sabit ? td.dataset.kart : undefined;
        if (!sabit) {
          const metin = (td.textContent ?? '').trim();
          const ogeVar = td.querySelector('button, a, input, select, img, svg, textarea') !== null;
          const secim = td.querySelector('input[type="checkbox"], input[type="radio"]') !== null && !metin;
          if (!metin && !ogeVar) tur = 'bos';
          else if (secim) tur = 'secim';
          else if (govde !== tablo.tFoot && yalnizEylem(td, ad)) tur = 'eylem';
          else if (!baslikVerildi && govde !== tablo.tFoot) { tur = 'baslik'; baslikVerildi = true; }
          else if (cokSutun && gorunen >= ana - 1 && !td.querySelector('button, a.ccd-btn')) tur = 'gizli';
          yaz(td, 'kart', tur);
        }
        if (tur === undefined) gorunen += 1;
        if (tur === 'gizli') gizliVar = true;
        yaz(td, 'etiket', ad && tur !== 'baslik' && tur !== 'eylem' && tur !== 'alt' ? ad : undefined);
      }
      // Satırın kendisi tıklanabiliyorsa (ayrıntı çekmecesi / kartı açıyor) "Ayrıntı" şeridi konmaz: gizli sütunlar orada görünür.
      const tiklanir = tr.classList.contains('ccd-tr-clickable') || tr.hasAttribute('tabindex') || tr.style.cursor === 'pointer'
        || (tr.dataset.kartAyrinti === undefined && getComputedStyle(tr).cursor === 'pointer');
      yaz(tr, 'kartAyrinti', gizliVar && !tiklanir ? '1' : undefined);
    }
  }
}

/**
 * Tabloyu saran kutuya satır içi `max-height` + `overflow` verilmişse (uzun tablo kendi kutusunda kayar),
 * kart kipinde bu iç kaydırma kaldırılır: kartlar sayfayla birlikte kayar. Sınıfla verilen sınırlar CSS'te
 * çözülür; satır içi stil CSS'ten ezilemediği için (kütüphane sözleşmesi: `!important` yok) burada alınır
 * ve telefon görünümünden çıkılınca geri konur.
 */
function kutuyuSerbestBirak(kap: HTMLElement | null) {
  if (!kap || kap.dataset.kartEskiYuk !== undefined || !kap.style.maxHeight) return;
  kap.dataset.kartEskiYuk = kap.style.maxHeight;
  kap.dataset.kartEskiTasma = kap.style.overflow;
  kap.style.maxHeight = 'none';
  kap.style.overflow = 'visible';
}
function kutulariGeriKoy() {
  for (const kap of document.querySelectorAll<HTMLElement>('[data-kart-eski-yuk]')) {
    kap.style.maxHeight = kap.dataset.kartEskiYuk ?? '';
    kap.style.overflow = kap.dataset.kartEskiTasma ?? '';
    delete kap.dataset.kartEskiYuk; delete kap.dataset.kartEskiTasma;
  }
}

/** Telefondaysa belgedeki bütün tabloları etiketler. Ucuzdur: yalnız değişen öznitelik yazılır. */
export function tablolariEtiketle() {
  if (!window.matchMedia(TELEFON).matches) { kutulariGeriKoy(); return; }
  for (const t of document.querySelectorAll<HTMLTableElement>('table.ccd-table')) tabloyuEtiketle(t);
}

/** Gizli sütunları olan karta dokununca ayrıntı açılır/kapanır (düğme, bağlantı, alan dokunuşları hariç). */
export function kartTiklamasi(e: MouseEvent) {
  const h = e.target as HTMLElement | null;
  if (!h || h.closest('button, a, input, select, textarea, label, summary, [role="button"]')) return;
  const tr = h.closest<HTMLElement>('tr[data-kart-ayrinti]');
  if (!tr) return;
  if (tr.dataset.kartAcik !== undefined) delete tr.dataset.kartAcik; else tr.dataset.kartAcik = '1';
}
